import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { anfrage, ApiFehler } from "../api/client";
import type { EinladungInfo, TokenAntwort } from "../api/typen";
import { Hinweis, Knopf, Spinner } from "../komponenten/Basis";
import { useAuth } from "../kontext/AuthKontext";

const MIN_LAENGE = 12;

/** Token steht im Link hinter „#“ (wird nie an einen Server geschickt). */
function tokenAusLink(): string | null {
  return window.location.hash.replace(/^#/, "").trim() || null;
}

export function EinladungSeite() {
  const { benutzer, sitzungUebernehmen } = useAuth();
  const navigate = useNavigate();
  const [token] = useState(tokenAusLink);
  const [info, setInfo] = useState<EinladungInfo | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [passwort, setPasswort] = useState("");
  const [wiederholung, setWiederholung] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    // Token aus Adresszeile und Verlauf entfernen – er liegt jetzt nur noch im State.
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  useEffect(() => {
    if (!token) return;
    anfrage<EinladungInfo>("/api/auth/einladung/pruefen", { methode: "POST", body: { token } })
      .then(setInfo)
      .catch((e: unknown) => setLadeFehler(e instanceof ApiFehler ? e.message : "Der Link konnte nicht geprüft werden."));
  }, [token]);

  // Wer schon angemeldet ist (und keinen Link öffnet), gehört nicht hierher.
  if (benutzer && !token) return <Navigate to="/" replace />;

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (passwort.length < MIN_LAENGE) return setFehler(`Das Passwort muss mindestens ${MIN_LAENGE} Zeichen lang sein.`);
    if (passwort !== wiederholung) return setFehler("Die beiden Passwörter stimmen nicht überein.");
    setLaedt(true);
    try {
      const antwort = await anfrage<TokenAntwort>("/api/auth/einladung/annehmen", { methode: "POST", body: { token, passwort } });
      sitzungUebernehmen(antwort);
      navigate(antwort.benutzer.rolle === "installateur" ? "/portal" : "/", { replace: true });
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Das Passwort konnte nicht gespeichert werden.");
    } finally {
      setLaedt(false);
    }
  }

  let inhalt;
  if (!token) {
    inhalt = <Hinweis art="fehler">Dieser Link ist unvollständig. Bitte öffnen Sie den Link genau so, wie Sie ihn erhalten haben.</Hinweis>;
  } else if (ladeFehler) {
    inhalt = <Hinweis art="fehler">{ladeFehler}</Hinweis>;
  } else if (!info) {
    inhalt = <div className="flex justify-center py-6"><Spinner label="Link wird geprüft…" /></div>;
  } else {
    inhalt = (
      <form onSubmit={absenden} className="space-y-4" noValidate>
        <p className="text-sm text-neutral-600">
          Hallo {info.name}, Ihr Zugang für <strong>{info.betrieb}</strong> ist bereit. Anmelden können Sie sich künftig mit{" "}
          <strong>{info.email}</strong> und dem Passwort, das Sie hier festlegen.
        </p>
        {/* Für Passwortmanager: E-Mail als (verstecktes) Benutzername-Feld */}
        <input type="email" autoComplete="username" value={info.email} readOnly hidden />
        <div>
          <label htmlFor="passwort" className="mb-1 block text-xs font-semibold">Neues Passwort</label>
          <input id="passwort" type="password" autoComplete="new-password" className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={passwort} onChange={(e) => setPasswort(e.target.value)} autoFocus />
          <p className="mt-1 text-xs text-neutral-500">Mindestens {MIN_LAENGE} Zeichen. Am besten einen Passwortmanager verwenden.</p>
        </div>
        <div>
          <label htmlFor="wiederholung" className="mb-1 block text-xs font-semibold">Passwort wiederholen</label>
          <input id="wiederholung" type="password" autoComplete="new-password" className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={wiederholung} onChange={(e) => setWiederholung(e.target.value)} />
        </div>
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        <Knopf variante="primaer" type="submit" className="w-full" laedt={laedt}>
          {info.erstes_passwort ? "Passwort festlegen und loslegen" : "Neues Passwort speichern"}
        </Knopf>
      </form>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 px-4 py-10">
      <div className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-8 shadow-karte">
        <img src="/logo.png" alt="AnmeldeMeister" className="h-6 w-auto" />
        <h1 className="mt-6 text-xl font-semibold">{info && !info.erstes_passwort ? "Passwort zurücksetzen" : "Zugang einrichten"}</h1>
        <div className="mt-4">{inhalt}</div>
      </div>
    </div>
  );
}
